import React, { type ReactNode, useCallback } from 'react';
import { AlertBanner, Box, Link } from '@saviynt/design-system';
import cx from 'classnames';

import { useChatOverlayContext } from '../../context/chatOverlayContext';
import { useIspmContext } from '../../context/ispmContext';
import { useIspmErrorContext } from '../../context/ispmErrorContext';
import { ErrorTypeEnum } from '../../data-layer/types';
import { useInvertedLocalize } from '../../utils/localization';
import { invertedMessages } from '../../utils/messages';

import './AlertBannerContainer.css';

function AlertBannerContainer() {
  const { ispmErrorState, updateIspmErrorState } = useIspmErrorContext();
  const { chatOverlayState, updateChatOverlayState } = useChatOverlayContext();
  const l = useInvertedLocalize(invertedMessages);

  const { ispmState } = useIspmContext();

  interface AlertBannerWrapperProps {
    role?: string;
    ariaLabel?: string;
    children: ReactNode;
  }

  const AlertBannerWrapper = useCallback(
    ({ children, role, ariaLabel }: AlertBannerWrapperProps) => (
      <Box
        className={cx(
          'Ispm-AlertBannerContainer',
          ispmState.isMenuPinned && 'Ispm-AlertBannerContainer--isMenuPinned'
        )}
        role={role}
        aria-label={ariaLabel}>
        {children}
      </Box>
    ),
    [ispmState]
  );

  function getAlertBanner() {
    // check for successes first.
    if (chatOverlayState.successAction) {
      switch (chatOverlayState.successAction.type) {
        case 'clipboardCopy':
          return (
            <AlertBannerWrapper
              role='alert'
              ariaLabel={l('Copied to clipboard')}>
              <AlertBanner
                kind='high'
                colorTheme='Information'
                title={l('Copied to clipboard')}
                description={l(
                  'The SQL code was copied to your clipboard. Paste it into SQL Lab to edit further.'
                )}
                // TODO: update copy above when we finalize the link.
                onCancel={() => {
                  updateChatOverlayState('successAction', undefined);
                }}
                isVisible
                LinkComponent={
                  /* eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus */
                  <Link
                    text='Go to SQL Lab'
                    suffixIconKind='ChevronRight'
                    color='neutralInverse'
                    onClick={() => {
                      // navigate to SQL Lab
                    }}
                  />
                }
                shouldAutoDismiss
                autoDismissDelay={7000} /* delay set in story AC */
              />
            </AlertBannerWrapper>
          );
        case 'downloadPending':
          return (
            <AlertBannerWrapper role='alert' ariaLabel='Download in Progress'>
              <AlertBanner
                kind='high'
                colorTheme='Information'
                title='Generating Data'
                description={l(
                  'Your data is being generated and will be available in the Downloads section of your profile menu when finished.'
                )}
                onCancel={() => {
                  updateChatOverlayState('successAction', undefined);
                }}
                isVisible
                shouldAutoDismiss
              />
            </AlertBannerWrapper>
          );

        default:
          return null;
      }
    }
    // Not a success action. check for error scenarios
    else if (ispmErrorState.errorType === ErrorTypeEnum.System) {
      return (
        <AlertBannerWrapper role='alert' aria-label={l('System Error Alert')}>
          <AlertBanner
            kind='high'
            colorTheme='Critical'
            title={l('Service temporary unavailable.')}
            description={l(
              'There was an error while processing your prompt. Please try again later.'
            )}
            onCancel={() => {
              updateIspmErrorState('errorType', undefined);
            }}
            isColonVisible={false}
            isVisible
          />
        </AlertBannerWrapper>
      );
    }

    return null;
  }

  return <>{getAlertBanner()}</>;
}

export default AlertBannerContainer;
